import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import posthog from 'posthog-js';
import { MessageCircle, ArrowRight, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import API_URL from '../../config';
import '../../pages/legal-pages.css';
import './ChatBot.css';

const WELCOME_COPY =
  'Your Compliance Intelligence Director is ready. Ask me about FMCSA regulations, your compliance standing, or anything DOT-related.';

const RYNN_UNAVAILABLE =
  'Rynn is temporarily unavailable. Please try again in a moment.';

function formatRynnError(error) {
  const raw = error.response?.data?.message;
  const message = Array.isArray(raw)
    ? raw.join(' ')
    : typeof raw === 'string'
      ? raw
      : '';

  if (
    !message ||
    /model:|anthropic|api_key|\.env|not configured/i.test(message)
  ) {
    return RYNN_UNAVAILABLE;
  }

  return message;
}

const SUGGESTIONS = [
  { label: 'Medical card renewal', question: 'How often do I need to update my medical card?' },
  { label: 'DOT inspection prep', question: 'What documents do I need for a DOT inspection?' },
  { label: 'IFTA filing process', question: 'How do I file IFTA quarterly?' },
  { label: 'MCS-150 updates', question: 'When is my MCS-150 due?' },
];

const mdComponents = {
  p: ({ children }) => <p>{children}</p>,
  strong: ({ children }) => <strong>{children}</strong>,
  ul: ({ children }) => <ul>{children}</ul>,
  ol: ({ children }) => <ol>{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
};

const ChatBot = ({ onClose, modalMode = false }) => {
  const [isOpen, setIsOpen] = useState(modalMode);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if ((isOpen || modalMode) && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, modalMode, isLoading, showWelcome]);

  useEffect(() => {
    if ((isOpen || modalMode) && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [isOpen, modalMode]);

  const askQuestion = (question) => {
    setInputValue(question);
    setShowWelcome(false);
    setTimeout(() => handleSendMessage(question), 0);
  };

  const handleSendMessage = async (prefilledMessage = null) => {
    const messageToSend = (prefilledMessage ?? inputValue).trim();
    if (!messageToSend || isLoading) return;

    if (!messages.some((m) => m.role === 'user')) {
      posthog.capture('rynn_conversation_started');
    }

    setInputValue('');
    setShowWelcome(false);

    const historyForApi = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    const newUserMessage = { role: 'user', content: messageToSend };
    setMessages((prev) => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(
        `${API_URL}/api/chatbot/message`,
        {
          message: messageToSend,
          conversationHistory: historyForApi,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      const botReply =
        response.data.reply || 'I apologize, but I was unable to generate a response.';
      setMessages((prev) => [...prev, { role: 'assistant', content: botReply }]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errText = formatRynnError(error);
      setMessages((prev) => [...prev, { role: 'assistant', content: errText }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMinimize = () => setIsOpen(false);
  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const welcomeBlock = showWelcome && messages.length === 0 && (
    <>
      <div className="rynn-chat__row rynn-chat__row--assistant">
        <div className="rynn-chat__assistant-col">
          <span className="rynn-chat__label">Rynn</span>
          <div className="rynn-chat__bubble rynn-chat__bubble--assistant">
            <div className="rynn-md">
              <ReactMarkdown components={mdComponents}>{WELCOME_COPY}</ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
      <div className="rynn-chat__suggestions">
        {SUGGESTIONS.map((s) => (
          <button
            key={s.label}
            type="button"
            className="rynn-chip"
            onClick={() => askQuestion(s.question)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <p className="rynn-chat__legal-disclaimer">
        Rynn provides compliance information for educational purposes only. Always verify requirements with
        FMCSA and qualified professionals.
      </p>
    </>
  );

  const messagesBlock = (
    <>
      {messages.map((message, index) =>
        message.role === 'user' ? (
          <div key={index} className="rynn-chat__row rynn-chat__row--user">
            <div className="rynn-chat__bubble rynn-chat__bubble--user">{message.content}</div>
          </div>
        ) : (
          <div key={index} className="rynn-chat__row rynn-chat__row--assistant">
            <div className="rynn-chat__assistant-col">
              <span className="rynn-chat__label">Rynn</span>
              <div className="rynn-chat__bubble rynn-chat__bubble--assistant">
                <div className="rynn-md">
                  <ReactMarkdown components={mdComponents}>{message.content}</ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        ),
      )}
      {isLoading && (
        <div className="rynn-chat__row rynn-chat__row--assistant">
          <div className="rynn-chat__assistant-col">
            <span className="rynn-chat__label">Rynn</span>
            <div className="rynn-chat__bubble rynn-chat__bubble--assistant">
              <div className="rynn-chat__loading" aria-hidden>
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </>
  );

  const inputBlock = (
    <div className="rynn-chat__input-wrap">
      <div className="rynn-chat__input-shell">
        <input
          ref={inputRef}
          type="text"
          className="rynn-chat__input"
          placeholder="Ask Rynn..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          aria-label="Message to Rynn"
        />
        <button
          type="button"
          className="rynn-chat__send"
          onClick={() => handleSendMessage()}
          disabled={isLoading || !inputValue.trim()}
          aria-label="Send message"
        >
          <ArrowRight size={18} strokeWidth={2.5} aria-hidden />
        </button>
      </div>
    </div>
  );

  const chatInner = (
    <div className="rynn-chat">
      <div className="rynn-chat__scroll">
        {welcomeBlock}
        {messagesBlock}
      </div>
      {inputBlock}
    </div>
  );

  if (modalMode) {
    return chatInner;
  }

  if (!isOpen) {
    return (
      <div className="rynn-chat-launcher-only">
        <button
          type="button"
          className="rynn-fab"
          onClick={() => setIsOpen(true)}
          title="Ask Rynn"
          aria-label="Ask Rynn"
        >
          <MessageCircle className="rynn-fab__icon" size={26} strokeWidth={2} />
        </button>
      </div>
    );
  }

  return (
    <div className="rynn-chat-window">
      <header className="rynn-panel__header">
        <div className="rynn-panel__title">
          <p className="rynn-panel__name">Rynn</p>
          <p className="rynn-panel__subtitle">Your Compliance Intelligence Director</p>
        </div>
        <div className="rynn-chat-window__header-actions">
          <button
            type="button"
            className="rynn-panel__close"
            onClick={handleMinimize}
            aria-label="Minimize"
          >
            <span className="rynn-panel__minimize-icon" aria-hidden>
              −
            </span>
          </button>
          <button type="button" className="rynn-panel__close" onClick={handleClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
      </header>
      {chatInner}
    </div>
  );
};

export default ChatBot;
